import React, { Fragment, useEffect, useState, useRef } from 'react'
import { Disclosure } from '@headlessui/react'
import WidgetHeader from '../../../../components/Widget/Header'
import { Button, Checkbox, Icon } from '@arnsbogroup/react-voicearchive'

const audioErrors = {
  1: 'Fetching process aborted by user.',
  2: 'Error occurred when downloading.',
  3: 'Error occurred when decoding.',
  4: 'Audio not supported.',
}

const DemoPlayer = ({ demo, label }) => {
  const [error, setError] = useState(null)

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="w-16 shrink-0 text-gray-500">{label}</span>
        <audio
          className="w-full h-8 min-w-0"
          onError={(e) => audioErrors[e.currentTarget.error.code] && setError(audioErrors[e.currentTarget.error.code])}
          controls
          controlsList="nodownload"
          src={demo.cdn_url || `/stream/supplier/demo/${demo.uid}`}
        >
          Your browser does not support the audio element. Please download and listen on an external player.
        </audio>
        <a
          href={`/download/supplier/demo/${demo.uid}`}
          target="_blank"
          className="shrink-0 text-gray-400 hover:text-primary"
          title={`Download ${demo.name || label}`}
        >
          <Icon icon={['fas', 'download']} className="fa-fw" />
        </a>
      </div>
      { error && <p className="ml-20 text-negative">{error} Please download the file.</p> }
    </div>
  )
}

const MissingDemo = ({ label }) => (
  <div className="flex items-center gap-3">
    <span className="w-16 shrink-0 text-gray-500">{label}</span>
    <span className="text-gray-400">Not uploaded</span>
  </div>
)

const QualityCheckWidget = ({widget}) => {
  const mounted = useRef(false)

  const [checks, setChecks] = useState([])
  const [saving, setSaving] = useState([])
  const [title, setTitle] = useState('')

  useEffect(() => {
    mounted.current = true
    fetch(`supplierdashboard/${widget.widgetId}`)
    .then((response) => response.json())
    .then((data) => {
      if (mounted.current) {
        setChecks(data.data.checks)
      }
    })

    setTitle(widget.widgetId.replace(/_/g, ' '))

    return () => {
      mounted.current = false
    }
  }, [])

  const putQualityCheck = (supplierId, body) =>
    fetch(`/rest/v2/supplier/${supplierId}/quality-check`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .then((response) => response.json())
    .then((result) => {
      if (result.errors && result.errors.length) {
        throw new Error(result.errors.join(', '))
      }

      return result
    })

  const setNotSoldWithoutPost = (check, notSoldWithoutPost) => {
    setChecks(checks.map((prev) => (
      prev.uid == check.uid ? { ...prev, not_sold_without_post: notSoldWithoutPost } : prev
    )))

    putQualityCheck(check.uid, { notSoldWithoutPost })
      .catch((error) => {
        window.alert(error)
        setChecks(checks.map((prev) => (
          prev.uid == check.uid ? { ...prev, not_sold_without_post: !notSoldWithoutPost } : prev
        )))
      })
  }

  const confirmations = {
    rejected: (check) => `Reject ${check.userid}? This blocks the talent and emails them our audio quality rejection.`,
    new_demos: (check) => `Request new demos from ${check.userid}? This moves the talent back to Onboarding and emails them the new demo specifications.`,
  }

  const submitQualityCheck = (check, outcome) => {
    if (confirmations[outcome] && !window.confirm(confirmations[outcome](check))) {
      return
    }

    setSaving([...saving, check.uid])

    putQualityCheck(check.uid, {
      outcome,
      notSoldWithoutPost: !!check.not_sold_without_post,
    })
    .then(() => setChecks(checks.filter(({ uid }) => uid != check.uid)))
    .catch((error) => window.alert(error))
    .then(() => setSaving(saving.filter((uid) => uid != check.uid)))
  }

  const widget_id = `ID_${widget.widgetId}`
  return (
    <li className="bg-white border m-4 shadow rounded border-gray-400" key={widget_id} id={widget_id}>
      <WidgetHeader widget={widget} widgetTitle={title} />
      <div className="w-full text-xs">
          <p className="border-b px-4 py-3 text-gray-500">
            Approve ticks the audio check; sourcing accepts after the native check.
          </p>
          { !checks.length ? (
            <p className="p-4 text-gray-400 text-center">
              No pending quality checks.
            </p>
          ) : checks.map((check) => (
            <div className="border-b last:border-b-0 p-4 space-y-3" key={check.uid}>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate" title={check.userid}>{check.firstname} {check.lastname}</p>
                <p className="text-gray-500 truncate">{check.userid}</p>
              </div>

              <div className="space-y-2">
                { check.studio_demo
                  ? <DemoPlayer demo={check.studio_demo} label="Studio" />
                  : <MissingDemo label="Studio" />
                }

                <Disclosure>
                  {({ open }) => (
                    <Fragment>
                      <Disclosure.Button className="flex items-center gap-1 text-gray-500 hover:text-primary">
                        <Icon icon={['fas', open ? 'chevron-down' : 'chevron-right']} className="fa-fw" />
                        <span>{open ? 'Hide audition demo' : 'Show audition demo'}</span>
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        { check.audition_demo
                          ? <DemoPlayer demo={check.audition_demo} label="Audition" />
                          : <MissingDemo label="Audition" />
                        }
                      </Disclosure.Panel>
                    </Fragment>
                  )}
                </Disclosure>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <Checkbox
                  checked={!!check.not_sold_without_post}
                  onChange={({ target: { checked } }) => setNotSoldWithoutPost(check, checked)}
                  label="Do not sell without post"
                  labelClassNames="text-xs whitespace-nowrap"
                />

                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    size="xs"
                    className="shrink-0"
                    variant="outline"
                    state="negative"
                    icon={['fas', 'times']}
                    title="Blocks the talent and emails the audio quality rejection"
                    onClick={() => submitQualityCheck(check, 'rejected')}
                    disabled={saving.includes(check.uid)}
                  >
                    Reject
                  </Button>

                  <Button
                    size="xs"
                    className="shrink-0"
                    variant="outline"
                    state="warning"
                    icon={['fas', 'redo']}
                    title="Moves the talent back to Onboarding and emails the new demo specifications"
                    onClick={() => submitQualityCheck(check, 'new_demos')}
                    disabled={saving.includes(check.uid)}
                  >
                    Request new demos
                  </Button>

                  <Button
                    size="xs"
                    className="shrink-0"
                    state="positive"
                    icon={['fas', 'check']}
                    title="Ticks the audio check in the onboarding overview"
                    onClick={() => submitQualityCheck(check, 'approved')}
                    disabled={saving.includes(check.uid)}
                  >
                    Approve
                  </Button>
                </div>
              </div>
            </div>
          ))}
      </div>
    </li>
  )

}

export default QualityCheckWidget
